import { useCallback, useRef, useState } from "react";

export type FileUploadPhase = "idle" | "uploading" | "processing";

export interface FileUploadProgressEvent {
  loaded: number;
  total?: number;
}

export interface FileUploadRequestOptions {
  onUploadProgress: (event: FileUploadProgressEvent) => void;
}

export default function useFileUploadProgress() {
  const [phase, setPhase] = useState<FileUploadPhase>("idle");
  const [progressPercentage, setProgressPercentage] = useState<number | null>(
    null,
  );

  const startUpload = useCallback(() => {
    setPhase("uploading");
    setProgressPercentage(0);
  }, []);

  const handleUploadProgress = useCallback(
    ({ loaded, total }: FileUploadProgressEvent) => {
      if (!total || total <= 0) {
        setPhase("uploading");
        setProgressPercentage(null);
        return;
      }

      const percentage = Math.min(
        100,
        Math.max(0, Math.round((loaded / total) * 100)),
      );

      setProgressPercentage(percentage);
      setPhase(percentage === 100 ? "processing" : "uploading");
    },
    [],
  );

  const resetUploadProgress = useCallback(() => {
    setPhase("idle");
    setProgressPercentage(null);
  }, []);

  return {
    phase,
    progressPercentage,
    startUpload,
    handleUploadProgress,
    resetUploadProgress,
  };
}

type ConcurrentUploadState = {
  completed: boolean;
  progress: number;
  totalKnown: boolean;
  weight: number;
};

export function useConcurrentFileUploadProgress() {
  const progress = useFileUploadProgress();
  const uploadsRef = useRef<ConcurrentUploadState[]>([]);

  const publishProgress = useCallback(() => {
    const uploads = uploadsRef.current;
    if (uploads.length === 0) {
      return;
    }

    const loaded = uploads.reduce(
      (total, upload) => total + upload.weight * upload.progress,
      0,
    );
    const total = uploads.reduce((sum, upload) => sum + upload.weight, 0);
    const hasUnknownTotal = uploads.some(
      (upload) => !upload.completed && !upload.totalKnown,
    );

    progress.handleUploadProgress(
      hasUnknownTotal ? { loaded } : { loaded, total },
    );
  }, [progress.handleUploadProgress]);

  const startUpload = useCallback(
    (files: readonly File[]) => {
      uploadsRef.current = files.map((file) => ({
        completed: false,
        progress: 0,
        totalKnown: true,
        weight: Math.max(file.size, 1),
      }));
      progress.startUpload();
    },
    [progress.startUpload],
  );

  const handleUploadProgress = useCallback(
    (fileIndex: number, event: FileUploadProgressEvent) => {
      const upload = uploadsRef.current[fileIndex];
      if (!upload || upload.completed) {
        return;
      }

      if (!event.total || event.total <= 0) {
        upload.totalKnown = false;
        publishProgress();
        return;
      }

      upload.totalKnown = true;
      upload.progress = Math.max(
        upload.progress,
        Math.min(1, Math.max(0, event.loaded / event.total)),
      );
      publishProgress();
    },
    [publishProgress],
  );

  const markUploadComplete = useCallback(
    (fileIndex: number) => {
      const upload = uploadsRef.current[fileIndex];
      if (!upload) {
        return;
      }

      upload.completed = true;
      upload.progress = 1;
      upload.totalKnown = true;
      publishProgress();
    },
    [publishProgress],
  );

  const resetUploadProgress = useCallback(() => {
    uploadsRef.current = [];
    progress.resetUploadProgress();
  }, [progress.resetUploadProgress]);

  return {
    phase: progress.phase,
    progressPercentage: progress.progressPercentage,
    startUpload,
    handleUploadProgress,
    markUploadComplete,
    resetUploadProgress,
  };
}
